import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import type { Operation } from '@occc-app/offerHelpers/types';
import {
  LimitConfigurationInput,
  LimitFragment,
  Limit_Time_Period,
  LimitCreateMutationVariables,
  LimitUpdateMutationVariables,
  OfferFragment,
} from '@occc/graphql';
import { limitHelper } from '@occc-app/offerHelpers/utils/limitHelper';

import type {
  AwardCountLimitFormValue,
  AwardCountLimitFormValueEnabled,
  AwardCountLimitRow,
  AwardCountLimitScope,
  AwardCountLimitTimePeriod,
} from './award-count-limit.types';

type InTimeAwardCountLimit = Extract<LimitFragment, { timePeriod: Limit_Time_Period }>;

type ScopeAdapter = {
  isTotal: (limit: LimitFragment) => boolean;
  isInTime: (limit: LimitFragment) => limit is InTimeAwardCountLimit;
  totalConfig: (limit: number) => LimitConfigurationInput;
  inTimeConfig: (limit: number, timePeriod: Limit_Time_Period) => LimitConfigurationInput;
};

const scopeAdapters: Record<AwardCountLimitScope, ScopeAdapter> = {
  membership: {
    isTotal: (limit) => limitHelper.isMembershipAwardCount(limit),
    isInTime: (limit): limit is InTimeAwardCountLimit =>
      limitHelper.isMembershipAwardCountInTime(limit),
    totalConfig: (limit) => ({ membershipAwardCount: { limit } }),
    inTimeConfig: (limit, timePeriod) => ({ membershipAwardCountInTime: { limit, timePeriod } }),
  },
  global: {
    isTotal: (limit) => limitHelper.isGlobalAwardCount(limit),
    isInTime: (limit): limit is InTimeAwardCountLimit =>
      limitHelper.isGlobalAwardCountInTime(limit),
    totalConfig: (limit) => ({ globalAwardCount: { limit } }),
    inTimeConfig: (limit, timePeriod) => ({ globalAwardCountInTime: { limit, timePeriod } }),
  },
};

const apiToFormTimePeriod: Record<Limit_Time_Period, AwardCountLimitTimePeriod> = {
  [Limit_Time_Period.Day]: 'DAY',
  [Limit_Time_Period.Week]: 'WEEK',
  [Limit_Time_Period.Month]: 'MONTH',
};

const formToApiTimePeriod: Record<
  Exclude<AwardCountLimitTimePeriod, 'TOTAL'>,
  Limit_Time_Period
> = {
  DAY: Limit_Time_Period.Day,
  WEEK: Limit_Time_Period.Week,
  MONTH: Limit_Time_Period.Month,
};

const fromApiTimePeriod = (timePeriod: Limit_Time_Period): AwardCountLimitTimePeriod =>
  apiToFormTimePeriod[timePeriod] ?? 'DAY';

const toApiTimePeriod = (period: Exclude<AwardCountLimitTimePeriod, 'TOTAL'>): Limit_Time_Period =>
  formToApiTimePeriod[period] ?? Limit_Time_Period.Day;

export const getAwardCountLimitInitialValues = (
  offer: OfferFragment,
  scope: AwardCountLimitScope,
): AwardCountLimitFormValue => {
  const adapter = scopeAdapters[scope];
  const rows: AwardCountLimitRow[] = [];

  for (const limit of offer.limits) {
    if (adapter.isTotal(limit)) {
      rows.push({ id: limit.id, limit: limit.limit, timePeriod: 'TOTAL' });
    } else if (adapter.isInTime(limit)) {
      rows.push({
        id: limit.id,
        limit: limit.limit,
        timePeriod: fromApiTimePeriod(limit.timePeriod),
      });
    }
  }

  return {
    isEnabled: rows.length > 0,
    rows: rows.length > 0 ? rows : [{ limit: 1, timePeriod: 'DAY' }],
  };
};

const getExistingScopeLimitIds = (limits: LimitFragment[], adapter: ScopeAdapter): Set<string> => {
  const ids = new Set<string>();
  for (const limit of limits) {
    if (adapter.isTotal(limit) || adapter.isInTime(limit)) {
      ids.add(limit.id);
    }
  }
  return ids;
};

const makeLimitCreateOperation = (
  offerId: string,
  configuration: LimitConfigurationInput,
): Operation<'limitCreate', LimitCreateMutationVariables> => ({
  name: 'limitCreate',
  variables: {
    input: { offerId, configuration },
  },
});

const makeLimitUpdateOperation = (
  limitId: string,
  configuration: LimitConfigurationInput,
): Operation<'limitUpdate', LimitUpdateMutationVariables> => ({
  name: 'limitUpdate',
  variables: {
    input: { id: limitId, configuration },
  },
});

const getOperationsForRow = (
  row: AwardCountLimitRow,
  currentLimits: LimitFragment[],
  offerId: string,
  adapter: ScopeAdapter,
): Operation[] => {
  const existing = row.id ? currentLimits.find((l) => l.id === row.id) : undefined;

  if (row.timePeriod === 'TOTAL') {
    const config = adapter.totalConfig(row.limit);
    if (!existing) {
      return [makeLimitCreateOperation(offerId, config)];
    }
    if (adapter.isTotal(existing) && existing.limit === row.limit) {
      return [];
    }
    if (adapter.isTotal(existing)) {
      return [makeLimitUpdateOperation(existing.id, config)];
    }
    return [
      makeDeleteOperation('limitDelete', existing.id),
      makeLimitCreateOperation(offerId, config),
    ];
  }

  const timePeriod = toApiTimePeriod(row.timePeriod);
  const config = adapter.inTimeConfig(row.limit, timePeriod);
  if (!existing) {
    return [makeLimitCreateOperation(offerId, config)];
  }
  if (
    adapter.isInTime(existing) &&
    existing.limit === row.limit &&
    existing.timePeriod === timePeriod
  ) {
    return [];
  }
  if (adapter.isInTime(existing)) {
    return [makeLimitUpdateOperation(existing.id, config)];
  }
  return [
    makeDeleteOperation('limitDelete', existing.id),
    makeLimitCreateOperation(offerId, config),
  ];
};

const isEnabledFormValue = (
  v: NonNullable<AwardCountLimitFormValue>,
): v is AwardCountLimitFormValueEnabled => v.isEnabled;

export const getAwardCountLimitOperations = (
  offerId: string,
  scope: AwardCountLimitScope,
  values: AwardCountLimitFormValue | undefined,
  currentLimits: LimitFragment[],
): Operation[] => {
  const adapter = scopeAdapters[scope];

  if (!values || !isEnabledFormValue(values)) {
    const operations: Operation[] = [];
    for (const limit of currentLimits) {
      if (adapter.isTotal(limit) || adapter.isInTime(limit)) {
        operations.push(makeDeleteOperation('limitDelete', limit.id));
      }
    }
    return operations;
  }

  const existingIds = getExistingScopeLimitIds(currentLimits, adapter);
  const rowIds = new Set(
    values.rows
      .filter((r): r is AwardCountLimitRow & { id: string } => r.id != null)
      .map((r) => r.id),
  );

  const operations: Operation[] = [];
  for (const id of existingIds) {
    if (!rowIds.has(id)) {
      operations.push(makeDeleteOperation('limitDelete', id));
    }
  }

  for (const row of values.rows) {
    operations.push(...getOperationsForRow(row, currentLimits, offerId, adapter));
  }

  return operations;
};
